export const ArticleStatus = {
  Draft: 0,
  Published: 2,
  // Retired: off the knowledge base and out of staff lists unless filtered for; restore returns it to Draft.
  // Set only through the archive action, never the edit form
  Archived: 3,
} as const;

export type ArticleStatus = typeof ArticleStatus[keyof typeof ArticleStatus];

export const ArticleStatusLabels: Record<ArticleStatus, string> = {
  [ArticleStatus.Draft]: 'Draft',
  [ArticleStatus.Published]: 'Published',
  [ArticleStatus.Archived]: 'Archived',
};

export const ArticleStatusColors: Record<ArticleStatus, string> = {
  [ArticleStatus.Draft]: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
  [ArticleStatus.Published]: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  [ArticleStatus.Archived]: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
};

export const ArticleVisibility = {
  Public: 0,
  Internal: 1,
} as const;

export type ArticleVisibility = typeof ArticleVisibility[keyof typeof ArticleVisibility];

export const ArticleVisibilityLabels: Record<ArticleVisibility, string> = {
  [ArticleVisibility.Public]: 'Public',
  [ArticleVisibility.Internal]: 'Internal',
};

export const ArticleVisibilityColors: Record<ArticleVisibility, string> = {
  [ArticleVisibility.Public]: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  [ArticleVisibility.Internal]: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
};

export interface ArticleDto {
  id: string;
  title: string;
  content: string;
  summary?: string;
  tags?: string;
  status: ArticleStatus;
  visibility: ArticleVisibility;
  // The knowledge base collection (or section) the article is filed under
  collectionId?: string;
  collectionName?: string;
  // "Collection › Section" for an article in a section
  collectionPath?: string;
  organizationId: string;
  organizationName: string;
  authorId: string;
  authorName: string;
  viewCount: number;
  lastViewedAt?: string;
  isFeatured: boolean;
  sortOrder: number;
  slug?: string;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
  archivedAt?: string | null;
  // A published article's unpublished changes, waiting in its draft (readers still see the live text)
  hasDraft?: boolean;
  draftUpdatedAt?: string | null;
  // A draft set to publish itself at this time
  scheduledPublishAt?: string | null;
  // When someone last confirmed the article is still right (publishing counts)
  lastReviewedAt?: string | null;
}

export interface CreateArticleRequest {
  title: string;
  content: string;
  summary?: string;
  tags?: string;
  status?: ArticleStatus;
  visibility?: ArticleVisibility;
  // The collection or section to file the article under
  collectionId?: string;
  isFeatured?: boolean;
  organizationId: string;  // Required for multi-org support
}

export interface UpdateArticleRequest {
  title: string;
  content: string;
  summary?: string;
  tags?: string;
  // Leave out to keep the status: an edit form sending back the status it loaded would undo a publish or unpublish
  // made in the meantime. Publishing and unpublishing have their own endpoints.
  status?: ArticleStatus;
  visibility?: ArticleVisibility;
  // The collection or section to file the article under
  collectionId?: string;
  isFeatured: boolean;
  // The updatedAt the editor loaded, sent back exactly as received. If the article changed since, the save is refused
  // with 409 instead of overwriting the other person's changes.
  expectedUpdatedAt?: string;
}

// ── Revisions ──

export const ArticleRevisionKind = {
  Created: 1,
  Saved: 2,
  Published: 3,
  Restored: 4,
} as const;

export type ArticleRevisionKind = typeof ArticleRevisionKind[keyof typeof ArticleRevisionKind];

export const ArticleRevisionKindLabels: Record<ArticleRevisionKind, string> = {
  [ArticleRevisionKind.Created]: 'Created',
  [ArticleRevisionKind.Saved]: 'Saved',
  [ArticleRevisionKind.Published]: 'Published',
  [ArticleRevisionKind.Restored]: 'Restored',
};

// A saved version of an article's text (title, summary, tags, content) - the newest 50 are kept
export interface ArticleRevisionSummary {
  id: string;
  revisionNumber: number;
  kind: ArticleRevisionKind;
  title: string;
  note?: string | null;
  createdAt: string;
  createdByName?: string | null;
}

export interface ArticleRevisionResponse extends ArticleRevisionSummary {
  summary?: string | null;
  tags?: string | null;
  content: string;
}

// ── Drafts of published articles ──

export interface ArticleDraftResponse {
  articleId: string;
  title: string;
  summary?: string | null;
  tags?: string | null;
  content: string;
  updatedAt: string;
  updatedByName?: string | null;
}

// The text waits in the draft; collection, visibility and featured apply to the live article at once
export interface SaveArticleDraftRequest {
  title: string;
  content: string;
  summary?: string;
  tags?: string;
  visibility: ArticleVisibility;
  collectionId?: string;
  isFeatured: boolean;
  // The draft's updatedAt the editor loaded (omit when there was none) - a draft changed since is a 409
  expectedDraftUpdatedAt?: string;
  // The article's updatedAt the editor loaded - checked when this save starts the draft
  expectedUpdatedAt?: string;
}

export interface PublishArticleDraftRequest {
  // The draft's updatedAt that was reviewed, so what goes live is what the publisher saw
  expectedDraftUpdatedAt?: string;
}

// ── Search insights ──

export interface SearchInsightQuery {
  query: string;
  searches: number;
  averageResults: number;
  // Searches that went on to open a result
  clicks: number;
  lastSearchedAt: string;
}

// What customers searched for in the portal (searches are kept 90 days)
export interface SearchInsightsResponse {
  days: number;
  totalSearches: number;
  searchesWithResults: number;
  searchesWithClicks: number;
  zeroResultSearches: number;
  topQueries: SearchInsightQuery[];
  // Searches that found nothing - articles worth writing
  zeroResultQueries: SearchInsightQuery[];
}

// ── Needs attention ──

export const ArticleAttentionReason = {
  NegativeFeedback: 'NegativeFeedback',
  DueForReview: 'DueForReview',
  NotRead: 'NotRead',
} as const;

export type ArticleAttentionReason = typeof ArticleAttentionReason[keyof typeof ArticleAttentionReason];

export interface ArticleNeedsAttentionItem {
  // A list item: no content
  article: ArticleDto;
  reasons: ArticleAttentionReason[];
  // Votes since the article was last reviewed
  helpfulVotes: number;
  notHelpfulVotes: number;
  reviewDueAt?: string | null;
}

export interface ArticleNeedsAttentionResponse {
  items: ArticleNeedsAttentionItem[];
  // Before the list was cut to 100
  totalCount: number;
}

export interface ArticleSearchRequest {
  query?: string;
  status?: ArticleStatus;
  visibility?: ArticleVisibility;
  // A collection includes its sections
  collectionId?: string;
  authorId?: string;
  isFeatured?: boolean;
  tags?: string;
  createdAfter?: string;
  createdBefore?: string;
  publishedAfter?: string;
  publishedBefore?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDescending?: boolean;
}

export interface ArticleStatsDto {
  totalArticles: number;
  publishedArticles: number;
  draftArticles: number;
  // Not included in totalArticles
  archivedArticles: number;
  totalViews: number;
  viewsThisMonth: number;
  viewsThisWeek: number;
  mostViewedArticles: ArticleDto[];
  recentlyPublished: ArticleDto[];
  featuredArticles: ArticleDto[];
}

export interface PublishArticleRequest {
  publishedAt?: string;
}

export interface BulkStatusUpdateRequest {
  articleIds: string[];
  status: ArticleStatus;
}

export interface BulkDeleteRequest {
  articleIds: string[];
}

export interface MoveToCollectionRequest {
  articleIds: string[];
  // Omit (or null) to leave the articles uncategorized
  collectionId?: string | null;
}

export interface MoveToCollectionResponse {
  movedCount: number;
  totalRequested: number;
  movedArticles: ArticleDto[];
  newCollectionId?: string | null;
  newCollectionName?: string | null;
}

export interface ArticleSearchResponse {
  articles: ArticleDto[];
  totalCount: number;
  pageCount: number;
  currentPage: number;
  pageSize: number;
}

export interface ArticleFilterRequest {
  page?: number;
  pageSize?: number;
  searchQuery?: string;
  statuses?: ArticleStatus[];
  visibilities?: ArticleVisibility[];
  // Each includes its sections
  collectionIds?: string[];
  organizationIds?: string[];
  authorId?: string;
  isFeatured?: boolean;
  tags?: string;
  createdAfter?: string;
  createdBefore?: string;
  sortBy?: string;
  sortDescending?: boolean;
}

export interface ArticleListResponse {
  articles: ArticleDto[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

// For full-text search results
export interface ArticleFullTextSearchResponse {
  articles: ArticleDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  query: string;
}

export interface ArticleViewRequest {
  articleId: string;
  userId?: string;
  ipAddress?: string;
  userAgent?: string;
}

// Customer Portal Knowledge Base Types
// An article in a portal list (collection page, search, popular) - no content; the article page loads that
export interface CustomerPortalArticleDto {
  id: string;
  title: string;
  slug?: string | null;
  summary?: string;
  tags?: string;
  // The knowledge base collection (or section) the article is filed under
  collectionId?: string;
  collectionName?: string;
  // "Collection › Section" for an article in a section
  collectionPath?: string;
  authorName: string;
  viewCount: number;
  isFeatured: boolean;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
}

// Where an article sits, for the portal's breadcrumb: the collection, or the section and the collection above it
export interface CustomerPortalCollectionReference {
  id: string;
  name: string;
  parentId?: string | null;
  parentName?: string | null;
}

export interface CustomerPortalArticleDetailDto {
  id: string;
  title: string;
  slug?: string | null;
  content: string;
  summary?: string;
  tags?: string;
  collection?: CustomerPortalCollectionReference | null;
  authorName: string;
  viewCount: number;
  isFeatured: boolean;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
}

export interface CustomerPortalArticleListRequest {
  page?: number;
  pageSize?: number;
  // A collection includes its sections
  collectionId?: string;
  // Only articles in no collection
  uncategorized?: boolean;
  isFeatured?: boolean;
  searchQuery?: string;
  sortBy?: string;
  sortDescending?: boolean;
}

export interface CustomerPortalArticleListResponse {
  articles: CustomerPortalArticleDto[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

export interface CustomerPortalSearchRequest {
  query: string;
  // A collection includes its sections
  collectionId?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDescending?: boolean;
}

export interface CustomerPortalSearchResponse {
  articles: CustomerPortalArticleDto[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  query: string;
  // The logged search (first page only) - sent back when the visitor opens a result
  searchId?: string | null;
}

// Ticket-Article Linking Types

export const ArticleLinkType = {
  Reference: 0,
  Solution: 1,
  Suggested: 2,
} as const;

export type ArticleLinkType = typeof ArticleLinkType[keyof typeof ArticleLinkType];

export const ArticleLinkTypeLabels: Record<ArticleLinkType, string> = {
  [ArticleLinkType.Reference]: 'Reference',
  [ArticleLinkType.Solution]: 'Solution',
  [ArticleLinkType.Suggested]: 'Suggested',
};

export const ArticleLinkTypeColors: Record<ArticleLinkType, string> = {
  [ArticleLinkType.Reference]: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  [ArticleLinkType.Solution]: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  [ArticleLinkType.Suggested]: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
};

export interface TicketArticleLinkResponse {
  id: string;
  ticketId: string;
  articleId: string;
  articleTitle: string;
  articleSummary?: string;
  articleStatus: ArticleStatus;
  articleVisibility: ArticleVisibility;
  linkType: ArticleLinkType;
  linkedByUserName: string;
  linkedByUserId: string;
  notes?: string;
  linkedAt: string;
}

export interface TicketArticleLinksResponse {
  ticketId: string;
  links: TicketArticleLinkResponse[];
  count: number;
}

export interface LinkArticleToTicketRequest {
  articleId: string;
  linkType: ArticleLinkType;
  notes?: string;
}

export interface UpdateTicketArticleLinkRequest {
  linkType: ArticleLinkType;
  notes?: string;
}

export interface ArticleTicketLinkSummary {
  ticketId: string;
  ticketNumber: number;
  subject: string;
  status: number;
  linkType: ArticleLinkType;
  linkedAt: string;
}

export interface ArticleTicketLinksResponse {
  articleId: string;
  ticketLinks: ArticleTicketLinkSummary[];
  count: number;
}

// Customer Portal Linked Articles (simplified, read-only)
export interface CustomerPortalLinkedArticleResponse {
  articleId: string;
  title: string;
  summary?: string;
  collectionName?: string;
}

export interface CustomerPortalLinkedArticlesResponse {
  articles: CustomerPortalLinkedArticleResponse[];
  count: number;
}

// Article Feedback Types
export interface SubmitArticleFeedbackRequest {
  isHelpful: boolean;
  comment?: string;
}

export interface ArticleFeedbackResponse {
  id: string;
  articleId: string;
  isHelpful: boolean;
  comment?: string;
  createdAt: string;
}

export interface ArticleFeedbackSummary {
  articleId: string;
  helpfulCount: number;
  notHelpfulCount: number;
  totalFeedback: number;
  helpfulPercentage: number;
}

export interface ArticleFeedbackListResponse {
  feedback: ArticleFeedbackResponse[];
  totalCount: number;
  summary: ArticleFeedbackSummary;
}